/**
 * POS Sales Sync Bar Component
 *
 * Displays sync status, next auto-sync time, and action buttons.
 * Le bouton "Synchroniser" est un dropdown avec deux options :
 * - depuis la derniere synchro (incremental)
 * - plage personnalisee a re-synchroniser
 */

import { useMemo, useState } from 'react';
import { RefreshCw, Clock, CheckCircle2, AlertTriangle, Play, Loader2, ChevronDown, History, CalendarRange } from 'lucide-react';
import {
  Button,
  Dropdown,
  DropdownItem,
  Modal,
  ModalFooter,
  DatePicker,
  formatDateParam,
  type DateRange,
} from '@/components/ui';
import { useFormatDate } from '@/hooks';
import type { SalesSyncStatus } from '@bstock/shared';

const MAX_RESYNC_DAYS = 90;

interface PosSalesSyncBarProps {
  syncStatus: SalesSyncStatus | undefined;
  isLoading: boolean;
  // Sync incremental (depuis le dernier batch)
  onSync: () => void;
  // Sync re-synchronisation manuelle (plage personnalisee, YYYY-MM-DD)
  onResync: (range: { from: string; to: string }) => void;
  isSyncing: boolean;
  showSync: boolean;
  // Generate movements
  onGenerateMovements: () => void;
  isGeneratingMovements: boolean;
  showGenerateMovements: boolean;
}

function getStatusIcon(status: string | undefined) {
  switch (status) {
    case 'COMPLETED':
      return <CheckCircle2 className="h-5 w-5 text-success-500" />;
    case 'IN_PROGRESS':
      return <Clock className="h-5 w-5 text-warning-500 animate-pulse" />;
    case 'FAILED':
      return <AlertTriangle className="h-5 w-5 text-danger-500" />;
    default:
      return <Clock className="h-5 w-5 text-neutral-400" />;
  }
}

function getStatusLabel(status: string | undefined): string {
  switch (status) {
    case 'COMPLETED':
      return 'Synchronise';
    case 'IN_PROGRESS':
      return 'En cours';
    case 'FAILED':
      return 'Echec';
    default:
      return 'En attente';
  }
}

export function PosSalesSyncBar({
  syncStatus,
  isLoading,
  onSync,
  onResync,
  isSyncing,
  showSync,
  onGenerateMovements,
  isGeneratingMovements,
  showGenerateMovements,
}: PosSalesSyncBarProps) {
  const { formatDateTimeShort } = useFormatDate();

  const [resyncOpen, setResyncOpen] = useState(false);
  const [resyncRange, setResyncRange] = useState<DateRange>({ start: null, end: null });
  const [resyncError, setResyncError] = useState<string | null>(null);

  const lastSyncStatus = syncStatus?.lastSync?.status;
  const lastSyncDate = syncStatus?.lastSync?.endDate ?? null;
  const receiptsCount = syncStatus?.lastSync?.receiptsCount ?? 0;

  const formatSyncDate = (dateString: string | null): string => {
    if (!dateString) return 'Jamais';
    return formatDateTimeShort(dateString);
  };

  const rangeDays = useMemo(() => {
    if (!resyncRange.start || !resyncRange.end) return null;
    const ms = resyncRange.end.getTime() - resyncRange.start.getTime();
    return Math.floor(ms / (1000 * 60 * 60 * 24)) + 1;
  }, [resyncRange]);

  const handleOpenResync = () => {
    setResyncRange({ start: null, end: null });
    setResyncError(null);
    setResyncOpen(true);
  };

  const handleConfirmResync = () => {
    if (!resyncRange.start || !resyncRange.end) {
      setResyncError('Veuillez choisir une plage de dates.');
      return;
    }
    if (rangeDays !== null && rangeDays > MAX_RESYNC_DAYS) {
      setResyncError(`La plage est limitee a ${MAX_RESYNC_DAYS} jours.`);
      return;
    }
    const from = formatDateParam(resyncRange.start);
    const to = formatDateParam(resyncRange.end);
    if (!from || !to) {
      setResyncError('Dates invalides.');
      return;
    }
    setResyncOpen(false);
    onResync({ from, to });
  };

  return (
    <div className="bg-white border border-neutral-200 rounded-xl p-4 shadow-sm">
      <div className="flex flex-wrap items-center gap-4">
        {/* Sync status */}
        <div className="flex items-center gap-2">
          {isLoading ? (
            <RefreshCw className="h-5 w-5 text-neutral-400 animate-spin" />
          ) : (
            getStatusIcon(lastSyncStatus)
          )}
          <div>
            <p className="text-sm font-medium text-dark-500">
              {getStatusLabel(lastSyncStatus)}
            </p>
            <p className="text-xs text-neutral-500">
              Derniere sync : {formatSyncDate(lastSyncDate)}
            </p>
          </div>
        </div>

        {/* Last sync stats */}
        {syncStatus?.lastSync && (
          <div className="border-l border-neutral-200 pl-4">
            <p className="text-2xl font-bold text-dark-500">
              {receiptsCount}
            </p>
            <p className="text-xs text-neutral-500">Tickets importes</p>
          </div>
        )}

        {/* Next sync info */}
        {syncStatus?.nextSyncAt && (
          <div className="border-l border-neutral-200 pl-4">
            <p className="text-sm text-dark-500">
              Prochaine sync auto
            </p>
            <p className="text-xs text-neutral-500">
              {formatSyncDate(syncStatus.nextSyncAt)}
            </p>
          </div>
        )}

        {/* Not initialized warning */}
        {syncStatus && !syncStatus.isInitialized && syncStatus.billApiConfigured && (
          <div className="border-l border-neutral-200 pl-4">
            <p className="text-sm text-warning-600">
              Sync non initialisee
            </p>
            <p className="text-xs text-neutral-500">
              Lancez une premiere synchronisation
            </p>
          </div>
        )}

        {/* Action buttons - pushed to the right */}
        <div className="flex items-center gap-2 ml-auto">
          {showSync && (
            <Dropdown
              align="right"
              trigger={
                <Button variant="primary" size="md" disabled={isSyncing}>
                  <RefreshCw className={`h-4 w-4 mr-2 ${isSyncing ? 'animate-spin' : ''}`} />
                  {isSyncing ? 'Synchronisation...' : 'Synchroniser'}
                  <ChevronDown className="h-4 w-4 ml-2" />
                </Button>
              }
            >
              <DropdownItem
                icon={<History className="h-4 w-4" />}
                onClick={onSync}
                disabled={isSyncing}
              >
                Depuis la derniere synchro
              </DropdownItem>
              <DropdownItem
                icon={<CalendarRange className="h-4 w-4" />}
                onClick={handleOpenResync}
                disabled={isSyncing}
              >
                Plage personnalisee...
              </DropdownItem>
            </Dropdown>
          )}

          {showGenerateMovements && (
            <Button
              onClick={onGenerateMovements}
              disabled={isGeneratingMovements}
              variant="secondary"
              size="md"
            >
              {isGeneratingMovements ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Play className="h-4 w-4 mr-2" />
              )}
              {isGeneratingMovements ? 'Generation...' : 'Generer mouvements'}
            </Button>
          )}
        </div>
      </div>

      <Modal
        isOpen={resyncOpen}
        onClose={() => setResyncOpen(false)}
        title="Re-synchroniser une plage"
        description="Les tickets deja importes seront ignores (deduplication par identifiant Bill)."
        size="md"
      >
        <div className="px-6 py-4 space-y-3">
          <label className="block text-xs font-medium text-neutral-600">Plage de dates</label>
          <DatePicker
            value={resyncRange}
            onChange={(range) => {
              setResyncRange(range);
              setResyncError(null);
            }}
            placeholder="Selectionner une plage"
          />
          {rangeDays !== null && (
            <p className="text-xs text-neutral-500">
              {rangeDays} jour{rangeDays > 1 ? 's' : ''} selectionnes (max {MAX_RESYNC_DAYS}).
            </p>
          )}
          {resyncError && (
            <p className="text-sm text-danger-600">{resyncError}</p>
          )}
        </div>
        <ModalFooter>
          <Button variant="secondary" onClick={() => setResyncOpen(false)}>
            Annuler
          </Button>
          <Button variant="primary" onClick={handleConfirmResync} disabled={isSyncing}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Lancer la re-synchronisation
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
}
